import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@betty/i18n';
import { formatAmount } from '@betty/toolkit/utils';
import {
  Box,
  Text,
  Input,
  Button,
  Checkbox,
  AmountInput,
  KeyboardSpacer,
} from '@betty/moonlight';
import {
  getEmailError,
  getPhoneError,
  getAmountError,
} from './PaymentInterac.utils';
import { PaymentInteracProps } from './PaymentInterac.types';
import usePaymentDetails from '../../hooks/usePaymentDetails';
import PaymentInteracSkeleton from './PaymentInteracSkeleton/PaymentInteracSkeleton';

function PaymentInterac({
  amount: initialAmount,
  minAmount,
  maxAmount,
  balance,
  buttonKey,
  errorContext = {},
  showAmountField = false,
  footerKey,
  onPayment,
  ...rest
}: PaymentInteracProps) {
  const { t } = useTranslation('wallet');
  const paymentDetails = usePaymentDetails();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [shouldSaveDetails, setShouldSaveDetails] = useState(true);
  const [amount, setAmount] = useState(`${initialAmount}`);
  const [loading, setLoading] = useState(false);
  const emailError = useMemo(() => getEmailError(email), [email]);
  const phoneError = useMemo(() => getPhoneError(phone), [phone]);
  const amountError = useMemo(
    () => getAmountError(amount, minAmount, maxAmount, balance),
    [amount, minAmount, maxAmount, balance],
  );
  const hasError = Boolean(emailError || phoneError || amountError);

  const renderError = (error?: string) =>
    error && (
      <Text
        variant="body-s"
        color="darkSurfaceError"
        paddingHorizontal="space-100"
        marginTop="space-100"
      >
        {error}
      </Text>
    );

  const onButtonPress = useCallback(async () => {
    if (hasError) {
      return;
    }

    setLoading(true);

    await onPayment({
      amount: Number(amount),
      email,
      phoneNumber: phone,
      savePaymentDetails: shouldSaveDetails,
    });

    setLoading(false);
  }, [amount, email, phone, shouldSaveDetails, hasError, onPayment]);

  useEffect(() => {
    if (paymentDetails.data) {
      const { email, phoneNumber } = paymentDetails.data;

      setEmail(email);
      setPhone(phoneNumber);
    }
  }, [paymentDetails.data]);

  if (paymentDetails.dataState === 'pending') {
    return (
      <PaymentInteracSkeleton
        showAmountField={showAmountField}
        showFooter={!!footerKey}
      />
    );
  }

  return (
    <Box {...rest}>
      <Input
        value={email}
        placeholderNS="wallet"
        placeholderKey="interacEmail"
        keyboardType="email-address"
        error={emailError}
        onChangeText={setEmail}
      />
      {renderError(emailError && t(emailError, errorContext))}
      <Input
        value={phone}
        placeholderNS="wallet"
        placeholderKey="interacPhone"
        keyboardType="phone-pad"
        marginTop="space-400"
        error={phoneError}
        onChangeText={setPhone}
      />
      {renderError(phoneError && t(phoneError, errorContext))}
      <Checkbox
        labelNS="wallet"
        labelKey="interacSaveDetails"
        paddingTop="space-200"
        marginTop="space-400"
        gap="space-100"
        checked={shouldSaveDetails}
        onChange={setShouldSaveDetails}
      />
      {showAmountField && (
        <>
          <AmountInput
            value={amount}
            placeholderNS="wallet"
            placeholderKey="interacAmount"
            marginTop="space-400"
            error={amountError}
            onChangeText={setAmount}
          />
          {renderError(
            amountError &&
              t(amountError, {
                ...errorContext,
                minAmount: minAmount ? formatAmount(minAmount) : undefined,
                maxAmount: maxAmount ? formatAmount(maxAmount) : undefined,
              }),
          )}
        </>
      )}
      <Button
        text={t(buttonKey, {
          amount: amountError ? '' : formatAmount(Number(amount)),
        })}
        loading={loading}
        disabled={hasError}
        marginTop="space-1000"
        onPress={onButtonPress}
      />
      {footerKey && (
        <Text
          variant="caption"
          locNS="wallet"
          locKey={footerKey}
          color="darkSurfaceMuted"
          paddingTop="space-300"
        />
      )}
      <KeyboardSpacer />
    </Box>
  );
}

export default memo(PaymentInterac);
